/* Google sign-in in the top bar.

   Optional: the app works the same signed out. When the server has a
   GOOGLE_CLIENT_ID, Google's own "Sign in with Google" button is shown
   (Google Identity Services). Google hands the page an ID token, the server
   checks it and sets an HttpOnly session cookie; the page never stores a
   token itself. Signed in, the button becomes the user's photo with a small
   menu to sign out. */

import { authConfig, currentUser, describeFailure, signInWithGoogle, signOut } from "./api";
import { byId, escapeHtml, toast } from "./dom";
import type { User } from "./types";

interface GsiButtonOptions {
  type: "standard" | "icon";
  theme?: "outline" | "filled_blue";
  size?: "small" | "medium" | "large";
  shape?: "pill" | "circle" | "rectangular";
  text?: "signin" | "signin_with";
}

interface Gsi {
  initialize(config: { client_id: string; callback: (r: { credential: string }) => void; ux_mode?: "popup"; auto_select?: boolean }): void;
  renderButton(parent: HTMLElement, options: GsiButtonOptions): void;
  disableAutoSelect(): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: Gsi } };
  }
}

const GSI_SRC = "https://accounts.google.com/gsi/client";
let clientId = "";
let gsiReady: Promise<Gsi> | null = null;
/** Closes the signed-in menu; null while signed out. */
let closeMenu: (() => void) | null = null;

/** Google's script, loaded once and only when sign-in is on. */
function loadGsi(): Promise<Gsi> {
  gsiReady ??= new Promise<Gsi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => (window.google ? resolve(window.google.accounts.id) : reject(new Error("Google sign-in didn't load.")));
    script.onerror = () => {
      gsiReady = null; // try again next time, e.g. after going back online
      reject(new Error("Google sign-in didn't load."));
    };
    document.head.appendChild(script);
  });
  return gsiReady;
}

function slot(): HTMLElement {
  return byId("account");
}

async function showSignedOut(): Promise<void> {
  const box = slot();
  box.innerHTML = `<div class="account-google"></div>`;
  box.hidden = false;
  try {
    const gsi = await loadGsi();
    gsi.initialize({ client_id: clientId, callback: onCredential, ux_mode: "popup", auto_select: false });
    // Phones: Google's round "G" only, so the top bar still fits.
    const narrow = window.matchMedia("(max-width: 560px)").matches;
    gsi.renderButton(box.querySelector<HTMLElement>(".account-google")!, narrow
      ? { type: "icon", shape: "circle", size: "medium", theme: "outline" }
      : { type: "standard", shape: "pill", size: "medium", theme: "outline", text: "signin" });
  } catch {
    box.hidden = true; // offline, or Google blocked: the app carries on signed out
  }
}

function showSignedIn(user: User): void {
  const box = slot();
  const label = user.name || user.email || "Signed in";
  const face = user.picture
    ? `<img src="${escapeHtml(user.picture)}" alt="" referrerpolicy="no-referrer" width="32" height="32">`
    : `<span class="account-initial">${escapeHtml(label.charAt(0).toUpperCase())}</span>`;
  box.innerHTML = `
    <button class="account-face" type="button" aria-haspopup="true" aria-expanded="false"
            title="${escapeHtml(label)}" aria-label="Account: ${escapeHtml(label)}">${face}</button>
    <div class="account-menu" role="menu" hidden>
      <p class="account-who"><b>${escapeHtml(user.name || "Signed in")}</b>${user.email ? `<small>${escapeHtml(user.email)}</small>` : ""}</p>
      <button class="btn soft small wide account-out" type="button" role="menuitem">Sign out</button>
    </div>`;
  box.hidden = false;
  const faceButton = box.querySelector<HTMLButtonElement>(".account-face")!;
  const menu = box.querySelector<HTMLElement>(".account-menu")!;
  closeMenu = (): void => {
    menu.hidden = true;
    faceButton.setAttribute("aria-expanded", "false");
  };
  faceButton.addEventListener("click", (event) => {
    event.stopPropagation();
    menu.hidden = !menu.hidden;
    faceButton.setAttribute("aria-expanded", String(!menu.hidden));
  });
  box.querySelector(".account-out")!.addEventListener("click", async () => {
    closeMenu?.();
    closeMenu = null;
    await signOut().catch(() => undefined);
    window.google?.accounts.id.disableAutoSelect();
    toast("Signed out.");
    void showSignedOut();
  });
}

async function onCredential(response: { credential: string }): Promise<void> {
  try {
    const user = await signInWithGoogle(response.credential);
    showSignedIn(user);
    toast(`Signed in as ${user.name || user.email}.`);
  } catch (error) {
    toast(describeFailure(error), "error");
  }
}

/** Show the sign-in button or the signed-in user. Quietly does nothing when
    sign-in is off or the server can't be reached. */
export async function initAuth(): Promise<void> {
  // The menu closes on a click elsewhere or Escape; wired once for every sign-in.
  document.addEventListener("click", (event) => {
    if (!slot().contains(event.target as Node)) closeMenu?.();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeMenu?.();
  });
  try {
    const config = await authConfig();
    if (!config.enabled) return;
    clientId = config.google_client_id;
    const user = await currentUser();
    if (user) showSignedIn(user);
    else await showSignedOut();
  } catch {
    // Signed-out is the default; nothing to tell the user.
  }
}
